import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`voorwaarden-${id}`} className="scroll-mt-28">
    <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-white mb-3">{title}</h2>
    <div className="space-y-3 text-sm sm:text-base text-slate-300 leading-relaxed">{children}</div>
  </section>
);

export default function Voorwaarden() {
  return (
    <div data-testid="voorwaarden-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
          <p className="font-mono-brand text-xs uppercase tracking-widest text-[#FF087B] mb-2">Juridisch</p>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-white leading-none">
            Algemene <span className="text-gradient-splat">voorwaarden</span>
          </h1>
          <p className="mt-4 text-slate-400 text-sm">
            Laatst bijgewerkt: 1 oktober 2026. Deze voorwaarden zijn opgesteld conform het Nederlandse
            Burgerlijk Wetboek (Boek 6 & 7) en de Europese richtlijn consumentenrechten (2011/83/EU).
          </p>
        </motion.div>

        <div className="space-y-10">
          <Section id="identiteit" title="1. Identiteit van de ondernemer">
            <p>Gelvaro (hierna: "Gelvaro", "wij" of "ons") is een webwinkel gevestigd in Nederland.</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300">
              <li>Handelsnaam: Gelvaro</li>
              <li>Vestigings- en bezoekadres: [straat + nummer], [postcode] [plaats], Nederland</li>
              <li>Telefoon: 085 060 4649 (bereikbaar op werkdagen) — ook via WhatsApp</li>
              <li>E-mail: info@gelvaro.nl</li>
              <li>KvK-nummer: [KvK-nummer invullen]</li>
              <li>BTW-identificatienummer: [BTW-nummer invullen]</li>
            </ul>
          </Section>

          <Section id="toepasselijkheid" title="2. Toepasselijkheid">
            <p>
              Deze algemene voorwaarden zijn van toepassing op elk aanbod van Gelvaro en op elke tot stand
              gekomen overeenkomst op afstand tussen Gelvaro en de consument. Voordat de overeenkomst wordt
              gesloten, wordt de tekst van deze voorwaarden langs elektronische weg beschikbaar gesteld, zodat
              deze eenvoudig kan worden opgeslagen.
            </p>
          </Section>

          <Section id="aanbod" title="3. Aanbod en prijzen">
            <p>
              Alle aanbiedingen zijn vrijblijvend en gelden zolang de voorraad strekt. Productafbeeldingen zijn een
              waarheidsgetrouwe weergave; kleine afwijkingen zijn mogelijk. Alle prijzen zijn in euro's en inclusief
              btw, tenzij anders vermeld. Kennelijke vergissingen of fouten in het aanbod binden Gelvaro niet.
            </p>
          </Section>

          <Section id="overeenkomst" title="4. De overeenkomst">
            <p>
              De overeenkomst komt tot stand op het moment dat de consument het aanbod aanvaardt en aan de
              gestelde voorwaarden voldoet. Na ontvangst van de bestelling bevestigt Gelvaro deze onverwijld
              langs elektronische weg (per e-mail). Zolang de ontvangst van deze aanvaarding niet is bevestigd,
              kan de consument de overeenkomst ontbinden.
            </p>
          </Section>

          <Section id="herroeping" title="5. Herroepingsrecht (14 dagen bedenktijd)">
            <p>
              Bij de aankoop van producten heeft de consument het recht de overeenkomst zonder opgave van redenen
              te ontbinden gedurende <strong className="text-white">14 dagen</strong>. Deze bedenktermijn gaat in op
              de dag nadat de consument (of een door hem aangewezen derde) het product heeft ontvangen.
            </p>
            <p>
              Tijdens de bedenktijd gaat de consument zorgvuldig om met het product en de verpakking. Hij pakt het
              product slechts uit of gebruikt het in de mate die nodig is om de aard en kenmerken vast te stellen.
              De consument is alleen aansprakelijk voor waardevermindering die het gevolg is van een verdergaande
              behandeling dan toegestaan.
            </p>
            <p>
              Wil je herroepen? Dat kan via je accountpagina met de knop <strong className="text-white">"Retour
              aanvragen"</strong>, per e-mail naar info@gelvaro.nl, of met het onderstaande modelformulier. We
              bevestigen de ontvangst van je herroeping per e-mail.
            </p>
            <p className="text-slate-400 text-sm">
              <strong className="text-white">Uitzonderingen:</strong> het herroepingsrecht geldt niet voor
              verzegelde producten die om redenen van gezondheid of hygiëne niet geschikt zijn om te worden
              teruggezonden, indien de verzegeling na levering is verbroken.
            </p>
          </Section>

          <Section id="kosten-herroeping" title="6. Terugbetaling en retourkosten">
            <p>
              Na ontbinding stuurt de consument het product binnen 14 dagen terug. Gelvaro vergoedt binnen 14 dagen
              na ontvangst van de melding van herroeping alle betalingen, inclusief eventuele leveringskosten (op
              basis van de goedkoopste standaardlevering), via hetzelfde betaalmiddel als waarmee de consument heeft
              betaald. De rechtstreekse kosten van het terugzenden komen voor rekening van de consument, tenzij
              anders aangegeven.
            </p>
          </Section>

          <Section id="levering" title="7. Levering en uitvoering">
            <p>
              Als plaats van levering geldt het adres dat de consument aan Gelvaro kenbaar heeft gemaakt. Bestellingen
              worden met bekwame spoed en uiterlijk binnen <strong className="text-white">30 dagen</strong> uitgevoerd,
              tenzij een andere leveringstermijn is overeengekomen. Bij vertraging ontvangt de consument hiervan
              bericht en heeft hij het recht de overeenkomst kosteloos te ontbinden. Het risico van beschadiging of
              vermissing ligt bij Gelvaro tot het moment van bezorging.
            </p>
          </Section>

          <Section id="betaling" title="8. Betaling">
            <p>
              Betaling verloopt via de in de webshop aangeboden methoden (o.a. iDEAL, Bancontact en creditcard) en
              wordt veilig afgehandeld via onze betaalprovider. De consument is verplicht onjuistheden in verstrekte
              of vermelde betaalgegevens onverwijld aan Gelvaro te melden.
            </p>
          </Section>

          <Section id="garantie" title="9. Conformiteit en garantie">
            <p>
              Gelvaro staat ervoor in dat de producten voldoen aan de overeenkomst, de in het aanbod vermelde
              specificaties en de wettelijke eisen. De consument heeft te allen tijde de rechten die voortvloeien uit
              de wettelijke (conformiteits)garantie. Een eventuele fabrieks- of handelsgarantie doet niets af aan deze
              wettelijke rechten.
            </p>
          </Section>

          <Section id="veiligheid" title="10. Veilig gebruik">
            <p>
              Gel blasters zijn recreatief speelgoed dat zachte, uiteenspattende water-orbs verschiet. Gebruik altijd
              oogbescherming, richt nooit op het gezicht en houd toezicht bij gebruik door kinderen. Gebruik is op
              eigen verantwoordelijkheid en dient te geschieden binnen de geldende lokale regelgeving.
            </p>
          </Section>

          <Section id="klachten" title="11. Klachten en geschillen">
            <p>
              Klachten over de uitvoering van de overeenkomst moeten binnen bekwame tijd nadat de consument de
              gebreken heeft geconstateerd, volledig en duidelijk omschreven worden ingediend bij info@gelvaro.nl.
              Ingediende klachten worden binnen 14 dagen beantwoord.
            </p>
            <p>
              Komen we er samen niet uit, dan kan de consument het geschil voorleggen via het Europese
              ODR-platform:{" "}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] underline">
                ec.europa.eu/consumers/odr
              </a>.
            </p>
          </Section>

          <Section id="persoonsgegevens" title="12. Persoonsgegevens">
            <p>
              Gelvaro verwerkt persoonsgegevens uitsluitend in overeenstemming met de Algemene Verordening
              Gegevensbescherming (AVG/GDPR) en gebruikt deze alleen voor het afhandelen van bestellingen,
              klantenservice en — met toestemming — het versturen van aanbiedingen. Gegevens worden niet aan derden
              verkocht.
            </p>
          </Section>

          <Section id="recht" title="13. Toepasselijk recht">
            <p>
              Op overeenkomsten tussen Gelvaro en de consument is uitsluitend Nederlands recht van toepassing. Dwingend
              consumentenrecht van het land waar de consument woont blijft onverkort van kracht.
            </p>
          </Section>

          <Section id="modelformulier" title="Bijlage: Modelformulier voor herroeping">
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 text-sm text-slate-300 space-y-2">
              <p className="text-slate-400">(Alleen invullen en terugsturen als je de overeenkomst wilt herroepen.)</p>
              <p>Aan: Gelvaro, info@gelvaro.nl</p>
              <p>Ik/Wij* deel/delen* u hierbij mede dat ik/wij* onze overeenkomst betreffende de verkoop van de volgende producten herroep/herroepen*:</p>
              <p>— Besteld op* / ontvangen op*: ..................</p>
              <p>— Naam consument(en): ..................</p>
              <p>— Adres consument(en): ..................</p>
              <p>— Ordernummer: ..................</p>
              <p>— Handtekening (alleen bij papieren formulier) en datum: ..................</p>
              <p className="text-slate-500 text-xs">* Doorhalen wat niet van toepassing is.</p>
            </div>
          </Section>

          <p className="text-xs text-slate-500 pt-4 border-t border-slate-800/60">
            Let op: vul de bedrijfsgegevens (adres, KvK- en btw-nummer) in voordat je live gaat. Laat deze
            voorwaarden bij voorkeur toetsen door een jurist voor publicatie.
          </p>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
